import { MouseEvent, useState } from 'react';

import { ReportFormat, useReportContext } from './ReportProvider';
import { MaterialIcon, ProButton } from '@components';
import { Flag } from '@utils';
import { Menu, MenuItem, Stack } from '@mui/material';
import { CustomAnalyticsEvents } from '@utils/analytics';
import { useFlagFixed } from '@hooks';
import { Controller, useFormContext } from 'react-hook-form';
import { ModifyActivityReportModel } from '@api';

export const FORMAT_OPTIONS = [
  { value: ReportFormat.CSV, label: 'Download CSV', icon: 'Csv' },
  { value: ReportFormat.PDF, label: 'Download PDF', icon: 'picture_as_pdf' },
];

export function DownloadReport() {
  const { control } = useFormContext<ModifyActivityReportModel>();
  const { isEnabled: isPDFExportEnabled } = useFlagFixed(Flag.REPORT_PDF_EXPORT);
  const { exportReportFile, isLoading } = useReportContext();

  return (
    <Controller
      name="schedule.format"
      control={control}
      defaultValue="csv"
      render={({ field: { onChange, value } }) =>
        isPDFExportEnabled ? (
          <DownloadReportSelectFormat onChange={onChange} isLoading={isLoading} exportReportFile={exportReportFile} />
        ) : (
          <ProButton
            variant="outlined"
            disabled={isLoading}
            flag={Flag.PREMIUM_DOWNLOAD_REPORT}
            endIcon={<MaterialIcon symbol="system_update_alt" />}
            onClick={() => exportReportFile(value)}
            data-trigger={CustomAnalyticsEvents.REPORT_EXPORT_BUTTON_CLICKED}
          >
            Download
          </ProButton>
        )
      }
    />
  );
}

function DownloadReportSelectFormat({
  onChange,
  isLoading,
  exportReportFile,
}: {
  onChange: (value: string) => void;
  isLoading: boolean;
  exportReportFile: (format: string) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (val: string) => {
    onChange(val);
    exportReportFile(val);
    handleClose();
  };

  return (
    <Stack direction="row" spacing={1}>
      <ProButton
        variant="outlined"
        disabled={isLoading}
        flag={Flag.PREMIUM_DOWNLOAD_REPORT}
        endIcon={<MaterialIcon symbol="system_update_alt" />}
        onClick={handleClick}
        data-trigger={CustomAnalyticsEvents.REPORT_EXPORT_BUTTON_CLICKED}
      >
        Download
      </ProButton>
      <Menu
        id="demo-positioned-menu"
        aria-labelledby="demo-positioned-button"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {FORMAT_OPTIONS.map((option) => (
          <MenuItem key={option.value} onClick={() => handleSelect(option.value)} sx={{ minWidth: 100 }}>
            <Stack direction="row" alignItems={'center'} gap={1}>
              <MaterialIcon symbol={option.icon} />
              {option.label}
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
}
